import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, IconButton, Stack, Text } from '@chakra-ui/react';
import { LuTrash2 } from 'react-icons/lu';
import { Modal } from '@/shared/ui/Modal';
import { formatDateTime } from '@/shared/lib/format';
import { webauthnApi } from '../api/webauthn';
import type { WebauthnCredentialSummary } from '../api/webauthn';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function WebauthnCredentialsModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState<WebauthnCredentialSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    void webauthnApi
      .listCredentials()
      .then(setCredentials)
      .catch(() => setError(t('webauthn.delete_error')));
  }, [open, t]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    setError(null);
    try {
      await webauthnApi.deleteCredential(id);
      setCredentials((prev) => (prev ? prev.filter((c) => c.id !== id) : prev));
    } catch {
      setError(t('webauthn.delete_error'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('webauthn.manage_title')}>
      <Stack gap="3">
        {error ? (
          <Box role="alert" color="red.fg" fontSize="sm">
            {error}
          </Box>
        ) : null}
        {credentials && credentials.length === 0 ? (
          <Text color="fg.muted" fontSize="sm">
            {t('webauthn.credentials_empty')}
          </Text>
        ) : null}
        {(credentials ?? []).map((cred) => (
          <HStack
            key={cred.id}
            justify="space-between"
            gap="3"
            wrap="wrap"
            borderWidth="1px"
            borderRadius="md"
            p="3"
          >
            <Box>
              <Text fontWeight="medium">{cred.name}</Text>
              <Text fontSize="xs" color="fg.muted">
                {t('webauthn.credential_created')}: {formatDateTime(cred.created_at)}
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {t('webauthn.credential_last_used')}:{' '}
                {cred.last_used_at
                  ? formatDateTime(cred.last_used_at)
                  : t('webauthn.credential_never_used')}
              </Text>
            </Box>
            <IconButton
              aria-label={t('webauthn.delete')}
              variant="ghost"
              colorPalette="red"
              loading={deletingId === cred.id}
              onClick={() => void handleDelete(cred.id)}
            >
              <LuTrash2 />
            </IconButton>
          </HStack>
        ))}
      </Stack>
    </Modal>
  );
}
